import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  SpeakerNotesOutlined as SpeakerNotesOutlinedIcon,
} from "@mui/icons-material";
import { selectedFilteredTerminalCall } from "../../selectors/filterSelector";
import { getLabels } from "../../data/labels";
import NotesPanel, {
  NoteItem,
  NoteValues,
} from "../notes/NotesPanel";

export type NotesObjectContext = {
  key: string;
  contextLabel: ReactNode;
  relationField: string;
  relationIri: string;
  notes?: NoteItem[];
  onNotesChange?: (
    notes: NoteItem[],
    changedNote?: NoteItem | null
  ) => Promise<void> | void;
};

type NotesSlideoutProviderProps = {
  children: ReactNode;
  showButton?: boolean;
  overlay?: boolean;
};

type NotesSlideoutContextValue = {
  notesContext: NotesObjectContext | null;
  setNotesContext: (context: NotesObjectContext | null) => void;
  openNotes: () => void;
  closeNotes: () => void;
};

const NotesSlideoutContext = createContext<NotesSlideoutContextValue | null>(
  null
);

const getEntityId = (entity: any) => entity?.["@id"] ?? entity?.id ?? null;

const getApiRequestUrl = (requestUrl: string) => {
  const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/g, "");
  const baseAlreadyIncludesApi =
    /\/api$/i.test(baseUrl) && /^\/api(?:\/|$)/i.test(requestUrl);
  const normalizedRequestUrl = baseAlreadyIncludesApi
    ? requestUrl.replace(/^\/api(?=\/|$)/i, "")
    : requestUrl;

  return /^https?:\/\//i.test(requestUrl)
    ? requestUrl
    : `${baseUrl}${normalizedRequestUrl.startsWith("/") ? "" : "/"}${
        normalizedRequestUrl
      }`;
};

const noteRequest = async (
  method: "POST" | "PATCH" | "DELETE",
  url: string,
  data?: Record<string, any>
) => {
  const response = await axios({
    method,
    url: getApiRequestUrl(url),
    headers: {
      Authorization: `Bearer ${localStorage.getItem("token")}`,
      ...(method === "PATCH"
        ? { "Content-Type": "application/merge-patch+json" }
        : method === "POST"
        ? { "Content-Type": "application/ld+json" }
        : {}),
    },
    data,
  });

  return response.data;
};

export function NotesSlideoutProvider({
  children,
  showButton = false,
  overlay = true,
}: NotesSlideoutProviderProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const selectedTerminalCall = useSelector(selectedFilteredTerminalCall);
  const labelOptions = useSelector((state: any) => state.labels?.data || []);
  const [registeredContext, setRegisteredContext] =
    useState<NotesObjectContext | null>(null);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<NoteItem[]>([]);

  const checklistNotesProgress = useMemo(
    () => {
      const checklistNotes = notes.filter(
        (note) => Array.isArray(note.checkList) && note.checkList.length > 0
      );
      const completed = checklistNotes.filter((note) =>
        note.checkList?.every((item) => item.done)
      ).length;

      return { completed, total: checklistNotes.length };
    },
    [notes]
  );
  const notesCountLabel = `${checklistNotesProgress.completed}/${checklistNotesProgress.total}`;
  const notesCountDescription =
    notes.length === 0
      ? t("addNote")
      : checklistNotesProgress.total > 0
        ? t("completedChecklistNotes", {
            count: checklistNotesProgress.completed,
            total: checklistNotesProgress.total,
          })
        : t("notes");

  const defaultContext = useMemo<NotesObjectContext | null>(() => {
    const relationIri = getEntityId(selectedTerminalCall);

    if (!relationIri) {
      return null;
    }

    return {
      key: String(relationIri),
      contextLabel: t("terminalCall"),
      relationField: "terminalCall",
      relationIri: String(relationIri),
      notes: Array.isArray(selectedTerminalCall?.notes)
        ? selectedTerminalCall.notes
        : [],
    };
  }, [selectedTerminalCall, t]);

  const activeContext = showButton ? registeredContext || defaultContext : null;

  useEffect(() => {
    setNotes(Array.isArray(activeContext?.notes) ? activeContext.notes : []);

    if (!activeContext) {
      setOpen(false);
    }
  }, [activeContext?.key, activeContext?.notes]);

  useEffect(() => {
    if (!activeContext) {
      return;
    }

    let mounted = true;
    const query = new URLSearchParams({
      [activeContext.relationField]: activeContext.relationIri,
    });

    axios
      .get(getApiRequestUrl(`/api/notes?${query.toString()}`), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          Accept: "application/ld+json",
        },
      })
      .then((response) => {
        const fetchedNotes = response.data?.["hydra:member"];

        if (mounted && Array.isArray(fetchedNotes)) {
          setNotes(fetchedNotes);
        }
      })
      .catch(() => undefined);

    return () => {
      mounted = false;
    };
  }, [activeContext?.key, activeContext?.relationField, activeContext?.relationIri]);

  useEffect(() => {
    if (activeContext && labelOptions.length === 0) {
      dispatch(getLabels() as any);
    }
  }, [activeContext?.key, dispatch, labelOptions.length]);

  const updateNotes = useCallback(
    async (nextNotes: NoteItem[], changedNote?: NoteItem | null) => {
      setNotes(nextNotes);
      await activeContext?.onNotesChange?.(nextNotes, changedNote);
    },
    [activeContext]
  );

  const handleCreate = useCallback(
    async (values: NoteValues) => {
      if (!activeContext) {
        return;
      }

      const createdNote = await noteRequest("POST", "/api/notes", {
        ...values,
        [activeContext.relationField]: activeContext.relationIri,
      });

      await updateNotes([...notes, createdNote], createdNote);
    },
    [activeContext, notes, updateNotes]
  );

  const handleUpdate = useCallback(
    async (note: NoteItem, values: NoteValues) => {
      const noteIri = getEntityId(note);

      if (!noteIri) {
        throw new Error(t("couldNotSaveNote"));
      }

      const savedNote = await noteRequest(
        "PATCH",
        String(noteIri),
        values
      );
      const nextNotes = notes.map((currentNote) =>
        getEntityId(currentNote) === getEntityId(savedNote)
          ? { ...currentNote, ...savedNote }
          : currentNote
      );

      await updateNotes(nextNotes, savedNote);
    },
    [notes, t, updateNotes]
  );

  const handleDelete = useCallback(
    async (note: NoteItem) => {
      const noteIri = getEntityId(note);

      if (!noteIri) {
        throw new Error(t("couldNotDeleteNote"));
      }

      await noteRequest("DELETE", String(noteIri));
      await updateNotes(
        notes.filter(
          (currentNote) => getEntityId(currentNote) !== noteIri
        ),
        null
      );
    },
    [notes, t, updateNotes]
  );

  const value = useMemo<NotesSlideoutContextValue>(
    () => ({
      notesContext: activeContext,
      setNotesContext: setRegisteredContext,
      openNotes: () => activeContext && showButton && setOpen(true),
      closeNotes: () => setOpen(false),
    }),
    [activeContext, showButton]
  );

  const panel = activeContext ? (
    <NotesPanel
      embedded
      title={
        <>
          {t("notes")} ({activeContext.contextLabel})
        </>
      }
      notes={notes}
      labelOptions={labelOptions}
      emptyText={t("noNotes")}
      addLabel={t("addNote")}
      noteLabel={t("note")}
      createLabel={t("save")}
      updateLabel={t("save")}
      cancelLabel={t("cancel")}
      deleteLabel={t("delete")}
      editLabel={t("edit")}
      onClose={() => setOpen(false)}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
    />
  ) : null;

  return (
    <NotesSlideoutContext.Provider value={value}>
      {showButton && !overlay ? (
        <Box
          sx={{
            display: "flex",
            width: "100%",
            minWidth: 0,
            minHeight: "100vh",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: "100vh",
              "& .MuiAppBar-positionFixed": activeContext && open
                ? {
                    width: { xs: 0, sm: "calc(100% - 410px)" },
                    right: { xs: "100%", sm: 410 },
                    transition: "width 200ms ease-in-out, right 200ms ease-in-out",
                  }
                : {
                    transition: "width 200ms ease-in-out, right 200ms ease-in-out",
                  },
            }}
          >
            {children}
          </Box>

          {activeContext ? (
            <Box
              component="aside"
              aria-hidden={!open}
              sx={(theme) => ({
                width: open ? { xs: "100%", sm: 410 } : 0,
                maxWidth: "100vw",
                flexShrink: 0,
                height: "100vh",
                overflow: "hidden",
                opacity: open ? 1 : 0,
                pointerEvents: open ? "auto" : "none",
                borderLeft: open
                  ? "1px solid rgba(15, 23, 42, 0.1)"
                  : "1px solid transparent",
                backgroundColor: "background.paper",
                transition: theme.transitions.create(
                  ["width", "opacity", "border-color"],
                  {
                    duration: theme.transitions.duration.short,
                    easing: theme.transitions.easing.easeInOut,
                  }
                ),
              })}
            >
              <Box
                sx={(theme) => ({
                  width: { xs: "100vw", sm: 410 },
                  height: "100%",
                  transform: open ? "translateX(0)" : "translateX(18px)",
                  transition: theme.transitions.create("transform", {
                    duration: theme.transitions.duration.short,
                    easing: theme.transitions.easing.easeInOut,
                  }),
                })}
              >
                {panel}
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : (
        children
      )}

      {activeContext ? (
        <>
          {!open ? (
            <Tooltip
              title={notesCountDescription}
              placement="left"
              arrow
            >
              <Box
                sx={(theme) => ({
                  position: "fixed",
                  top: "52%",
                  right: 0,
                  transform: "translateY(-50%)",
                  zIndex: theme.zIndex.drawer + 2,
                  borderRadius: "10px 0 0 10px",
                  border: `1px solid ${theme.palette.primary.main}24`,
                  borderRight: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  backdropFilter: "blur(10px)",
                  boxShadow: "-4px 4px 14px rgba(15, 23, 42, 0.13)",
                  overflow: "hidden",
                  opacity: 0.85,
                  transition: theme.transitions.create(
                    ["opacity", "box-shadow"],
                    { duration: 180, easing: theme.transitions.easing.easeOut }
                  ),
                  "&:hover, &:focus-within": {
                    opacity: 1,
                    boxShadow: "-7px 5px 20px rgba(15, 23, 42, 0.18)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: "50%",
                    width: 2,
                    height: 38,
                    borderRadius: 999,
                    backgroundColor: "primary.main",
                    transform: "translateY(-50%)",
                    opacity: 0.8,
                  },
                })}
              >
                <IconButton
                  aria-label={notesCountDescription}
                  onClick={() => setOpen(true)}
                  sx={(theme) => ({
                    width: 38,
                    height: 104,
                    minWidth: 38,
                    px: 0.2,
                    py: 0.75,
                    borderRadius: "10px 0 0 10px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    gap: 0.7,
                    color: "primary.main",
                    background:
                      "linear-gradient(145deg, rgba(25, 118, 210, 0.11), rgba(255, 255, 255, 0.2) 62%)",
                    transition: theme.transitions.create(
                      ["background-color", "color"],
                      { duration: theme.transitions.duration.shortest }
                    ),
                    "&:hover": {
                      color: "primary.dark",
                      background:
                        "linear-gradient(145deg, rgba(25, 118, 210, 0.17), rgba(255, 255, 255, 0.35) 64%)",
                    },
                  })}
                >
                  <SpeakerNotesOutlinedIcon sx={{ fontSize: 18 }} />
                  {notes.length === 0 ? (
                    <AddCircleOutlineIcon sx={{ fontSize: 15 }} />
                  ) : checklistNotesProgress.total > 0 ? (
                    <Box
                      component="span"
                      sx={(theme) => ({
                        minWidth: 27,
                        height: 16,
                        px: 0.35,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "common.white",
                        backgroundColor:
                          checklistNotesProgress.completed ===
                          checklistNotesProgress.total
                            ? theme.palette.success.main
                            : theme.palette.primary.main,
                        fontSize: "0.56rem",
                        lineHeight: 1,
                        fontWeight: 900,
                        fontVariantNumeric: "tabular-nums",
                      })}
                    >
                      {notesCountLabel}
                    </Box>
                  ) : null}
                </IconButton>
              </Box>
            </Tooltip>
          ) : null}

          {overlay ? (
            <Drawer
              anchor="right"
              variant="temporary"
              open={open}
              onClose={() => setOpen(false)}
              transitionDuration={200}
              sx={(theme) => ({
                zIndex: theme.zIndex.drawer + 1,
                "& .MuiDrawer-paper": {
                  width: { xs: "100%", sm: 410 },
                  maxWidth: "100vw",
                  top: 0,
                  height: "100%",
                  overflow: "hidden",
                  borderLeft: "1px solid rgba(15, 23, 42, 0.1)",
                  boxShadow: "-12px 0 32px rgba(15, 23, 42, 0.14)",
                },
              })}
            >
              {panel}
            </Drawer>
          ) : null}
        </>
      ) : null}
    </NotesSlideoutContext.Provider>
  );
}

export function useNotesSlideout() {
  const context = useContext(NotesSlideoutContext);

  if (!context) {
    throw new Error(
      "useNotesSlideout must be used inside NotesSlideoutProvider"
    );
  }

  return context;
}
